import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
    Button,
    Dropdown,
    Empty,
    Input,
    Modal,
    Pagination,
    Popconfirm,
    Spin,
    Tag,
    Typography,
    message,
} from "antd";
import {
    CopyOutlined,
    DeleteOutlined,
    EditOutlined,
    PlayCircleOutlined,
    QrcodeOutlined,
    SearchOutlined,
    UnorderedListOutlined,
} from "@ant-design/icons";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import BackHomeIcon, { PANEL_PATH } from "../../components/icons/BackHome/backhome";
import VideoReviewFormFields from "../../components/videoReview/VideoReviewFormFields";
import VideoReviewSharePanel from "../../components/videoReview/VideoReviewSharePanel";
import copyToClipboard from "../../utils/copy";
import { buildVideoReviewUrl } from "../../utils/videoReviewToken";
import {
    deleteVideoReview,
    listVideoReviewComments,
    listVideoReviews,
    updateVideoReview,
} from "../../utils/videoReviewAdminApi";
import {
    commentsToSrt,
    commentsToVtt,
    downloadTextFile,
    formatVideoTimestamp,
    sanitizeFilename,
} from "../../utils/videoReviewFormat";
import { videoReviewToFormValues, validateVideoReviewForm } from "../../utils/videoReviewForm";
import { useTranslation } from "../../i18n";
import "../delivery/delivery.css";

const { Paragraph, Text } = Typography;

const DEFAULT_PAGE_SIZE = 20;

function escapeCsvCell(value) {
    const str = value == null ? "" : String(value);
    if (/[",\n\r]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}

function VideoReviewManage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [searchText, setSearchText] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [loadingList, setLoadingList] = useState(false);
    const [editing, setEditing] = useState(null);
    const [posterItem, setPosterItem] = useState(null);
    const [editError, setEditError] = useState("");
    const [saving, setSaving] = useState(false);

    const loadItems = useCallback(async () => {
        setLoadingList(true);
        try {
            const result = await listVideoReviews({ page, pageSize, search: appliedSearch });
            if (result.items.length === 0 && page > 1 && result.total > 0) {
                setPage(Math.max(1, Math.ceil(result.total / pageSize)));
                return;
            }
            setItems(result.items);
            setTotal(result.total);
        } catch {
            message.error(t("videoReview.admin.manageLoadFailed"));
        } finally {
            setLoadingList(false);
        }
    }, [appliedSearch, page, pageSize, t]);

    useEffect(() => {
        loadItems();
    }, [loadItems]);

    const handleSearch = useCallback(() => {
        setAppliedSearch(searchText.trim());
        setPage(1);
    }, [searchText]);

    const handleCopy = useCallback(
        async (id) => {
            try {
                await copyToClipboard(buildVideoReviewUrl(id));
                message.success(t("videoReview.admin.linkCopied"));
            } catch {
                message.error(t("videoReview.admin.copyFailed"));
            }
        },
        [t]
    );

    const handleDelete = useCallback(
        async (id) => {
            try {
                await deleteVideoReview(id);
                message.success(t("videoReview.admin.deleteDone"));
                loadItems();
            } catch {
                message.error(t("videoReview.admin.deleteFailed"));
            }
        },
        [loadItems, t]
    );

    const openEdit = useCallback((item) => {
        setEditError("");
        setEditing({ id: item.id, values: videoReviewToFormValues(item) });
    }, []);

    const handleEditField = useCallback((field, value) => {
        setEditing((prev) =>
            prev ? { ...prev, values: { ...prev.values, [field]: value } } : prev
        );
    }, []);

    const handleSave = useCallback(async () => {
        if (!editing) {
            return;
        }
        setEditError("");

        let normalized;
        try {
            normalized = validateVideoReviewForm(editing.values, t);
        } catch (err) {
            setEditError(err.message || t("videoReview.admin.editFailed"));
            return;
        }

        setSaving(true);
        try {
            await updateVideoReview(editing.id, normalized);
            message.success(t("videoReview.admin.editDone"));
            setEditing(null);
            loadItems();
        } catch (err) {
            setEditError(err.message || t("videoReview.admin.editFailed"));
        } finally {
            setSaving(false);
        }
    }, [editing, loadItems, t]);

    const handleExportSubtitle = useCallback(
        async (item, format) => {
            try {
                const comments = await listVideoReviewComments(item.id);
                if (!comments.length) {
                    message.warning(t("videoReview.results.noComments"));
                    return;
                }
                const base = sanitizeFilename(item.videoName);
                if (format === "srt") {
                    downloadTextFile(`${base}.srt`, commentsToSrt(comments), { bom: true });
                } else if (format === "vtt") {
                    downloadTextFile(`${base}.vtt`, commentsToVtt(comments));
                } else {
                    const header = [
                        t("videoReview.results.colTimestamp"),
                        t("videoReview.results.colReviewer"),
                        t("videoReview.results.colBody"),
                        t("videoReview.results.colSubmittedAt"),
                    ];
                    const rows = comments.map((comment) => [
                        formatVideoTimestamp(comment.timestampMs),
                        comment.reviewerName,
                        comment.body,
                        dayjs(comment.createdAt * 1000).format("YYYY-MM-DD HH:mm:ss"),
                    ]);
                    const csv = [header, ...rows]
                        .map((row) => row.map(escapeCsvCell).join(","))
                        .join("\n");
                    downloadTextFile(`${base}-comments.csv`, csv, { bom: true });
                }
                message.success(t("videoReview.results.exportDone"));
            } catch {
                message.error(t("videoReview.results.exportFailed"));
            }
        },
        [t]
    );

    return (
        <>
            <PageTitle>
                <Navigation />
            </PageTitle>
            <BackHomeIcon to={PANEL_PATH} />

            <div className="delivery-page">
                <div className="custom-h1">
                    <span>{t("videoReview.admin.manageTitle")}</span>
                </div>
                <Paragraph
                    style={{
                        textAlign: "center",
                        color: "var(--color-text-subtle)",
                        fontSize: "0.9rem",
                        maxWidth: 560,
                        margin: "0 auto 1.5rem",
                    }}
                >
                    {t("videoReview.admin.manageSubtitle")}
                </Paragraph>

                <div className="delivery-admin-section">
                    <div
                        className="delivery-admin-actions"
                        style={{ marginBottom: "1rem", justifyContent: "space-between", flexWrap: "wrap", gap: "0.75rem" }}
                    >
                        <Button
                            icon={<UnorderedListOutlined />}
                            onClick={() => navigate("/v/results")}
                        >
                            {t("videoReview.admin.viewResults")}
                        </Button>
                        <Input.Search
                        size="large"
                            allowClear
                            value={searchText}
                            onChange={(event) => setSearchText(event.target.value)}
                            onSearch={handleSearch}
                            enterButton={<SearchOutlined />}
                            placeholder={t("videoReview.admin.searchPlaceholder")}
                            style={{ maxWidth: 320 }}
                        />
                    </div>

                    {loadingList ? (
                        <div style={{ textAlign: "center", padding: "1.5rem 0" }}>
                            <Spin />
                        </div>
                    ) : items.length === 0 ? (
                        <Empty description={t("videoReview.admin.manageEmpty")} />
                    ) : (
                        <div className="delivery-admin-list">
                            {items.map((item) => (
                                <div key={item.id} className="delivery-admin-list__item">
                                    <div className="delivery-admin-list__main">
                                        <div className="delivery-admin-list__title">
                                            <Text strong>
                                                {item.videoName ||
                                                    t("videoReview.gallery.defaultVideoName")}
                                            </Text>
                                            <Tag color={item.expired ? "default" : "green"}>
                                                {item.expired
                                                    ? t("videoReview.admin.expiredTag")
                                                    : t("videoReview.admin.validTag")}
                                            </Tag>
                                            <Tag>
                                                {item.commentVisibility === "individual"
                                                    ? t("videoReview.admin.visibilityIndividual")
                                                    : t("videoReview.admin.visibilityShared")}
                                            </Tag>
                                        </div>
                                        <Text type="secondary" className="delivery-admin-list__meta">
                                            {item.videoPath}
                                        </Text>
                                        <Text type="secondary" className="delivery-admin-list__meta">
                                            {t("videoReview.admin.expiresAt", {
                                                time: dayjs(item.expiry * 1000).format(
                                                    "YYYY-MM-DD HH:mm"
                                                ),
                                            })}
                                        </Text>
                                        <Text type="secondary" className="delivery-admin-list__meta">
                                            {t("videoReview.admin.viewCount", {
                                                count: item.viewCount ?? 0,
                                            })}
                                            {" · "}
                                            {t("videoReview.admin.commentCount", {
                                                count: item.commentCount ?? 0,
                                            })}
                                        </Text>
                                    </div>
                                    <div className="delivery-admin-list__actions">
                                        <Button
                                            size="small"
                                            icon={<PlayCircleOutlined />}
                                            onClick={() => navigate(`/v/audit/${item.id}`)}
                                        >
                                            {t("videoReview.audit.reviewButton")}
                                        </Button>
                                        <Button
                                            size="small"
                                            icon={<EditOutlined />}
                                            onClick={() => openEdit(item)}
                                        >
                                            {t("videoReview.admin.edit")}
                                        </Button>
                                        <Button
                                            size="small"
                                            icon={<CopyOutlined />}
                                            onClick={() => handleCopy(item.id)}
                                        >
                                            {t("videoReview.admin.copyLink")}
                                        </Button>
                                        <Button
                                            size="small"
                                            icon={<QrcodeOutlined />}
                                            onClick={() => setPosterItem(item)}
                                        >
                                            {t("videoReview.admin.getPoster")}
                                        </Button>
                                        <Dropdown
                                            menu={{
                                                items: [
                                                    {
                                                        key: "csv",
                                                        label: t("videoReview.results.exportCsv"),
                                                        onClick: () => handleExportSubtitle(item, "csv"),
                                                    },
                                                    {
                                                        key: "srt",
                                                        label: t("videoReview.results.exportSrt"),
                                                        onClick: () => handleExportSubtitle(item, "srt"),
                                                    },
                                                    {
                                                        key: "vtt",
                                                        label: t("videoReview.results.exportVtt"),
                                                        onClick: () => handleExportSubtitle(item, "vtt"),
                                                    },
                                                ],
                                            }}
                                            disabled={!item.commentCount}
                                        >
                                            <Button size="small">
                                                {t("videoReview.results.exportSubtitle")}
                                            </Button>
                                        </Dropdown>
                                        <Popconfirm
                                            title={t("videoReview.admin.deleteConfirm")}
                                            okText={t("videoReview.admin.deleteOk")}
                                            cancelText={t("videoReview.admin.deleteCancel")}
                                            onConfirm={() => handleDelete(item.id)}
                                        >
                                            <Button size="small" danger icon={<DeleteOutlined />} />
                                        </Popconfirm>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {!loadingList && total > 0 && (
                        <div style={{ display: "flex", justifyContent: "center", marginTop: "1.25rem" }}>
                            <Pagination
                                current={page}
                                pageSize={pageSize}
                                total={total}
                                showSizeChanger
                                pageSizeOptions={[10, 20, 50, 100]}
                                onChange={(nextPage, nextPageSize) => {
                                    setPage(nextPage);
                                    setPageSize(nextPageSize);
                                }}
                                showTotal={(totalCount, range) =>
                                    t("videoReview.admin.pageRange", {
                                        from: range[0],
                                        to: range[1],
                                        total: totalCount,
                                    })
                                }
                            />
                        </div>
                    )}
                </div>
            </div>

            <Modal
                open={Boolean(editing)}
                title={t("videoReview.admin.editTitle")}
                onCancel={() => !saving && setEditing(null)}
                footer={[
                    <Button key="cancel" disabled={saving} onClick={() => setEditing(null)}>
                        {t("videoReview.admin.deleteCancel")}
                    </Button>,
                    <Button key="save" type="primary" loading={saving} onClick={handleSave}>
                        {t("videoReview.admin.save")}
                    </Button>,
                ]}
                width={560}
                destroyOnClose
            >
                {editing && (
                    <>
                        <Paragraph type="secondary" style={{ fontSize: "0.82rem" }}>
                            ID: {editing.id}
                        </Paragraph>
                        <VideoReviewFormFields values={editing.values} onChange={handleEditField} />
                        {editError && <Paragraph type="danger">{editError}</Paragraph>}
                    </>
                )}
            </Modal>

            <Modal
                open={Boolean(posterItem)}
                title={t("videoReview.admin.posterTitle")}
                onCancel={() => setPosterItem(null)}
                footer={null}
                width={560}
                destroyOnClose
            >
                {posterItem && (
                    <>
                        <Paragraph type="secondary" style={{ marginBottom: "1rem" }}>
                            {t("videoReview.admin.posterHint")}
                        </Paragraph>
                        <VideoReviewSharePanel
                            url={buildVideoReviewUrl(posterItem.id)}
                            videoName={
                                posterItem.videoName || t("videoReview.gallery.defaultVideoName")
                            }
                            expiresAtLabel={dayjs(posterItem.expiry * 1000).format(
                                "YYYY-MM-DD HH:mm"
                            )}
                            compact
                        />
                    </>
                )}
            </Modal>

            <Footer />
        </>
    );
}

export default VideoReviewManage;
